import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowCounterClockwise, CircleNotch, CloudArrowUp, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

export function BackupSection() {
  const { toast } = useToast();
  const restoreInputRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [localBackups, setLocalBackups] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const fetchLocalBackups = useCallback(async () => {
    try {
      const { data } = await api.get("/fm/local-backups");
      setLocalBackups(data.backups);
    } catch {
      /* list stays empty */
    }
  }, []);

  useEffect(() => {
    fetchLocalBackups();
  }, [fetchLocalBackups]);

  const handleDownload = async () => {
    setBusy("download");
    try {
      const { data, headers } = await api.get("/fm/products/export-cache", { responseType: "blob" });
      const match = /filename="?([^"]+)"?/.exec(headers["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "product-cache-backup.json.gz";
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Backup downloaded", description: "Restore it on another environment via Settings > Backup > Restore" });
    } catch (e) {
      toast({ title: "Backup failed", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
    setBusy(null);
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("restore");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/fm/products/import-cache", form);
      toast({ title: `Restored ${data.imported} products from backup` });
    } catch (err) {
      toast({ title: "Restore failed", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
    setBusy(null);
  };

  const handleLocalBackupNow = async () => {
    setBusy("local-backup");
    try {
      const { data } = await api.post("/fm/local-backups");
      toast({ title: `Backup created - ${data.total_docs} records saved` });
      fetchLocalBackups();
    } catch (e) {
      toast({ title: "Backup failed", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
    setBusy(null);
  };

  const handleLocalRestore = async () => {
    const target = restoreTarget;
    setRestoreTarget(null);
    setBusy("local-restore");
    try {
      const { data } = await api.post("/fm/local-backups/restore", { filename: target.filename });
      toast({ title: `Restored ${data.total_docs} records from ${new Date(data.created_at).toLocaleString()}` });
    } catch (e) {
      toast({ title: "Restore failed", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
    setBusy(null);
  };

  return (
    <div className="flex max-w-2xl flex-col gap-6" data-testid="settings-backup-section">
      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Download Data Backup</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Exports the entire product catalog cache as a small backup file (~0.5 MB). Photos are stored
          separately and shared across environments, so this file alone can fully restore the catalog.
        </p>
        <Button
          size="sm"
          className="mt-4 rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          disabled={!!busy}
          onClick={handleDownload}
          data-testid="settings-download-backup-button"
        >
          {busy === "download" ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <DownloadSimple size={14} className="mr-1.5" />}
          Download Backup
        </Button>
      </div>

      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Restore Data Backup</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Imports a previously downloaded backup file and restores all products in seconds - no FileMaker
          sync needed. Existing products are updated in place, never wiped.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="mt-4 rounded-sm border-zinc-300"
          disabled={!!busy}
          onClick={() => restoreInputRef.current?.click()}
          data-testid="settings-restore-backup-button"
        >
          {busy === "restore" ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <UploadSimple size={14} className="mr-1.5" />}
          Restore from Backup File
        </Button>
        <input
          ref={restoreInputRef}
          type="file"
          accept=".gz,.json,application/gzip"
          className="hidden"
          onChange={handleRestore}
          data-testid="settings-restore-file-input"
        />
      </div>

      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Local Data Auto-Backup</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Users, carts, tags, API tokens, email quotes and GT selling history are automatically backed
          up to cloud storage every day at 3:00 AM ET (last 30 kept). Backups survive database resets
          and are shared between preview and production - either environment can restore them.
        </p>
        <Button
          size="sm"
          className="mt-4 rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          disabled={!!busy}
          onClick={handleLocalBackupNow}
          data-testid="local-backup-now-button"
        >
          {busy === "local-backup" ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <CloudArrowUp size={14} className="mr-1.5" />}
          Back Up Now
        </Button>
        {localBackups.length > 0 && (
          <Table className="mt-4" data-testid="local-backups-table">
            <TableHeader>
              <TableRow className="border-b border-zinc-300">
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Created</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Records</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {localBackups.map((b) => (
                <TableRow key={b.filename} className="border-b border-zinc-200">
                  <TableCell className="text-sm">{new Date(b.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm font-mono-data">{b.total_docs}</TableCell>
                  <TableCell className="text-sm capitalize">{b.trigger}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm border-zinc-300"
                      disabled={!!busy}
                      onClick={() => setRestoreTarget(b)}
                      data-testid={`local-restore-button-${b.filename}`}
                    >
                      <ArrowCounterClockwise size={14} className="mr-1.5" /> Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!restoreTarget} onOpenChange={(v) => !v && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="local-restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore local data backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Restores {restoreTarget?.total_docs} records (users, carts, tags, API tokens...) from{" "}
              {restoreTarget && new Date(restoreTarget.created_at).toLocaleString()}. Existing records are
              updated in place - nothing is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="local-restore-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleLocalRestore}
              data-testid="local-restore-confirm-button"
            >
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
